import React, { useMemo, useRef, useState } from 'react';
import { PackageIcon, PlusIcon, SearchIcon, WrenchIcon } from 'lucide-react';
import { JobMaterial, Material, Product } from '../../types/pro';
import { kr, newLineId } from '../../utils/pro';
interface JobLineAdderProps {
  materials: Material[];
  products: Product[];
  onAdd: (line: JobMaterial) => void;
}
interface Option {
  key: string;
  name: string;
  itemNo: string;
  unit: string;
  salesPrice: number;
  costPrice: number;
  /** Where it came from, so the job line can point back. */
  source: 'material' | 'product';
  id: string;
  hint: string;
}
const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
function normalize(value: string): string {
  return value.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa');
}

/**
 * One box for everything you put on a job. Materials and services used to
 * live in separate lists, which meant kørsel and servicebil could only be
 * added once the invoice was already open — too late to remember them.
 */
export function JobLineAdder({
  materials,
  products,
  onAdd
}: JobLineAdderProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Option | null>(null);
  const [quantity, setQuantity] = useState('1');
  const inputRef = useRef<HTMLInputElement>(null);
  const options = useMemo<Option[]>(() => [...products.map(product => ({
    key: `p-${product.id ?? product.productNo}`,
    name: product.name,
    itemNo: product.productNo,
    unit: product.unit,
    salesPrice: product.price,
    costPrice: 0,
    source: 'product' as const,
    id: product.id ?? '',
    hint: 'Ydelse'
  })), ...materials.map(material => ({
    key: `m-${material.id ?? material.itemNo}`,
    name: material.name,
    itemNo: material.itemNo,
    unit: material.unit,
    salesPrice: material.salesPrice,
    costPrice: material.costPrice,
    source: 'material' as const,
    id: material.id ?? '',
    hint: 'Materiale'
  }))], [products, materials]);
  const results = useMemo(() => {
    const term = normalize(query.trim());
    if (!term) return options.slice(0, 8);
    return options.filter(option => normalize(`${option.name} ${option.itemNo} ${option.unit}`).includes(term)).slice(0, 10);
  }, [options, query]);
  const choose = (option: Option) => {
    setPicked(option);
    setQuery(option.name);
    setOpen(false);
  };
  const submit = () => {
    const amount = parseFloat(quantity.replace(',', '.'));
    if (!picked || !amount) return;
    onAdd({
      id: newLineId(),
      ...(picked.source === 'material' ? {
        materialId: picked.id
      } : {
        productId: picked.id
      }),
      name: picked.name,
      itemNo: picked.itemNo,
      unit: picked.unit,
      quantity: amount,
      costPrice: picked.costPrice,
      salesPrice: picked.salesPrice
    });
    setPicked(null);
    setQuery('');
    setQuantity('1');
    inputRef.current?.focus();
  };

  /** The handful you reach for on almost every job. */
  const quick = options.filter(option => ['Servicebil', 'Kørsel', 'Småmaterialer'].includes(option.name));
  return <div data-id="element-411" data-mp="y4hf9tpiute5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_130px_auto]" data-id="element-412" data-mp="y36abqkcbflg">
        <div className="relative" data-id="element-413" data-mp="u22wtaprlvne">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" data-id="element-414" data-mp="u347he2vcg4l" />
          <input ref={inputRef} value={query} onChange={e => {
          setQuery(e.target.value);
          setPicked(null);
          setOpen(true);
        }} onFocus={() => setOpen(true)} onBlur={() => window.setTimeout(() => setOpen(false), 150)} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (picked) submit();else if (results[0]) choose(results[0]);
          }
          if (e.key === 'Escape') setOpen(false);
        }} placeholder="Søg materiale eller ydelse…" aria-label="Materiale eller ydelse" className={`${field} pl-9`} data-id="element-415" data-mp="p5m5k7n9r163" />

          {open && results.length > 0 && <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-stone-200 bg-white py-1 shadow-lg" data-id="element-416" data-mp="1g83zig46f7ro">
              {results.map(option => <li key={option.key} data-id="element-417" data-mp="16e4wzg06xlz7">
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => choose(option)} className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-stone-50" data-id="element-418" data-mp="1ba2f9rv0l2em">
                    {option.source === 'product' ? <WrenchIcon className="h-3.5 w-3.5 flex-shrink-0 text-brand-600" data-id="element-419" data-mp="3jih2tu1iuw8d" /> : <PackageIcon className="h-3.5 w-3.5 flex-shrink-0 text-stone-400" data-id="element-420" data-mp="1clzeqqkq8dz" />}
                    <span className="min-w-0 flex-1" data-id="element-421" data-mp="3six2esf59j7y">
                      <span className="block truncate text-sm text-stone-900" data-id="element-422" data-mp="3sh2ed58ktazn">
                        {option.name}
                      </span>
                      <span className="block truncate text-xs text-stone-400" data-id="element-423" data-mp="30ssbeawvpxxr">
                        {option.hint} · {option.itemNo}
                      </span>
                    </span>
                    <span className="flex-shrink-0 text-xs tabular-nums text-stone-500" data-id="element-424" data-mp="39s5k31d6iihf">
                      {kr(option.salesPrice)}/{option.unit}
                    </span>
                  </button>
                </li>)}
            </ul>}
        </div>

        <div className="flex items-center gap-2" data-id="element-425" data-mp="l0v8b02xgf83">
          <input value={quantity} onChange={e => setQuantity(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} inputMode="decimal" aria-label="Antal" className={field} data-id="element-426" data-mp="l02borxon63o" />
          <span className="w-10 flex-shrink-0 text-xs text-stone-500" data-id="element-427" data-mp="zq9n4np9vouv">
            {picked?.unit ?? ''}
          </span>
        </div>

        <button onClick={submit} disabled={!picked} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400" data-id="element-428" data-mp="23kcjpgi83xe">
          <PlusIcon className="h-4 w-4" data-id="element-429" data-mp="g3bt9xyenyf3" />
          Tilføj
        </button>
      </div>

      {quick.length > 0 && !picked && <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-430" data-mp="1nhotqso30sbx">
          <span className="text-xs text-stone-400" data-id="element-431" data-mp="1rfcp1wtosn4c">Hurtigt:</span>
          {quick.map(option => <button key={option.key} onClick={() => onAdd({
        id: newLineId(),
        ...(option.source === 'material' ? {
          materialId: option.id
        } : {
          productId: option.id
        }),
        name: option.name,
        itemNo: option.itemNo,
        unit: option.unit,
        quantity: 1,
        costPrice: option.costPrice,
        salesPrice: option.salesPrice
      })} className="rounded-md bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-200" data-id="element-432" data-mp="3by6qj78qv3kl">
              + {option.name}
            </button>)}
        </div>}
    </div>;
}